/**
 * filterToTargetOrg — BLU-669
 *
 * THE canonical owner filter for cross-org inventory write surfaces (Receive
 * Stock, Individual Stocktake, Adjust Unavailable, bulk upload preview).
 *
 * `getProducts` at a shared 3PL location legitimately returns rows for EVERY
 * accessible org (caller + connected clients). A write modal, however, routes
 * to exactly ONE target org — offering another org's SKUs under that target
 * is a guaranteed backend rejection ("belongs to a different organization",
 * inventory.service.ts resolveRowOrgForWrite). This helper narrows a product
 * list to the picked target:
 *
 *  - No target picked (single-tenant, or picker not yet set): list unchanged —
 *    the SKU dropdown is gated behind the picker at shared locations anyway.
 *  - Own-org target: rows the caller owns. `ownerOrganizationId === undefined`
 *    is treated as own-org for legacy rows written before the stamp existed
 *    (fail-open toward single-tenant, matching the pre-existing filter this
 *    was extracted from — UnavailableAdjustmentModal).
 *  - Client target: rows stamped with exactly that client's org id.
 *
 * NOTE for consumers: the filter is only meaningful when every producer of the
 * product list carries `ownerOrganizationId` through its mapping — dropping it
 * silently re-admits every row via the legacy fallback.
 */
import type { Product } from '../types';

export function filterToTargetOrg(
  products: Product[],
  targetOrgId: string | null | undefined,
  ownOrgId: string | undefined,
): Product[] {
  if (!targetOrgId || !ownOrgId) return products;
  if (targetOrgId === ownOrgId) {
    return products.filter(
      (p) => !p.ownerOrganizationId || p.ownerOrganizationId === ownOrgId,
    );
  }
  return products.filter((p) => p.ownerOrganizationId === targetOrgId);
}
